// Event/occurrence domain helpers shared by Calendar, Home, Notifications and
// the Event focus/create UI. Nothing in here knows about FullCalendar.
//
// Event      = details + series settings (title, RSVPs, recurrence, timezone).
// Occurrence = one scheduled instance of an Event ({ id, event_id, starts_at,
//              ends_at, ... }). Generated by the backend — never expanded here.

import { allDayDays, dayInZone, formatDay } from "./eventTime";
import { isCalendarVisible, isDeclinedOnItsOwn, occurrenceParticipation } from "./occurrenceParticipation";

// Mirrors backend Event.Recurrence choices.
export const RECURRENCE_OPTIONS = [
  { value: "none", label: "Does not repeat" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

// Mirrors backend EventRSVP.REMINDER_CHOICES. Reminders live on the current
// user's RSVP (my_rsvp.reminder) and only fire for "going" RSVPs.
export const REMINDER_OPTIONS = [
  { value: "none", label: "None" },
  { value: "15_min", label: "15 minutes before" },
  { value: "30_min", label: "30 minutes before" },
  { value: "1_hour", label: "1 hour before" },
  { value: "1_day", label: "1 day before" },
];

export function recurrenceLabel(value) {
  return RECURRENCE_OPTIONS.find((o) => o.value === value)?.label || "";
}

export function isRecurring(eventOrOccurrence) {
  return Boolean(eventOrOccurrence?.recurrence) && eventOrOccurrence.recurrence !== "none";
}

// EventSerializer.my_rsvp is { status, reminder } or null.
export function myRsvpStatus(event) {
  return event?.my_rsvp?.status ?? null;
}

export function myReminder(event) {
  return event?.my_rsvp?.reminder ?? "none";
}

// One-line summary of a start/end pair. Timed events are shown in browser
// local time. All-day events are calendar days in the event's timezone
// (`timeZone`; browser-local when omitted), stored as midnight → the
// midnight after the last day, so the last day shown is the day before the
// stored (exclusive) end.
export function formatEventRange(startsAt, endsAt, isAllDay = false, timeZone = undefined) {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";

  const dateOpts = { weekday: "short", month: "short", day: "numeric" };
  const timeOpts = { hour: "numeric", minute: "2-digit" };
  const sameDay = (a, b) => a.toDateString() === b.toDateString();

  if (isAllDay) {
    const { firstDay, lastDay } = allDayDays(startsAt, endsAt, timeZone);
    if (firstDay === lastDay) return `${formatDay(firstDay, dateOpts)} · All day`;
    return `${formatDay(firstDay, dateOpts)} – ${formatDay(lastDay, dateOpts)} · All day`;
  }

  const startText = `${start.toLocaleDateString(undefined, dateOpts)}, ${start.toLocaleTimeString(
    undefined,
    timeOpts
  )}`;
  if (sameDay(start, end)) return `${startText} – ${end.toLocaleTimeString(undefined, timeOpts)}`;
  return `${startText} – ${end.toLocaleDateString(undefined, dateOpts)}, ${end.toLocaleTimeString(
    undefined,
    timeOpts
  )}`;
}

// ---------------------------------------------------------------------------
// Occurrences: effective values, roles and available actions.
// ---------------------------------------------------------------------------

// An occurrence's start alone, for payloads without an end (the occurrence
// preview and friends' Home cards): "Tue, Oct 28, 3:00 PM", or an all-day
// event's first day in its timezone, "Tue, Oct 28 · All day".
export function formatEventStart(startsAt, isAllDay = false, timeZone = undefined) {
  const start = new Date(startsAt);
  if (Number.isNaN(start.getTime())) return "";
  const dateOpts = { weekday: "short", month: "short", day: "numeric" };
  if (isAllDay) return `${formatDay(dayInZone(startsAt, timeZone), dateOpts)} · All day`;
  return `${start.toLocaleDateString(undefined, dateOpts)}, ${start.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

// The route of an occurrence's preview (OccurrencePreview, App.js): what every
// Home event-start card and event engagement notification opens.
export const occurrencePreviewPath = (occurrenceId) => `/occurrences/${occurrenceId}`;

export const OCCURRENCE_CONTENT_FIELDS = ["title", "description", "location", "is_private"];

export function isCanceled(occurrence) {
  return occurrence?.status === "canceled";
}

// What an occurrence actually is: its own overrides over the series values,
// and its own times. THE one place overrides are resolved — render from this,
// never from an occurrence's top-level content fields (those are always the
// series values).
//
// `event` (full EventSerializer object, optional) supplies the series values
// when loaded — fresher than the copy echoed on the occurrence. Without an
// occurrence, the Event is shown at its own dates.
//
// A missing/null override inherits; any other value — including "" for
// description/location — is this occurrence's own. A canceled occurrence has
// every value frozen into its overrides by the backend.
export function effectiveOccurrence({ event = null, occurrence = null }) {
  const series = event ?? occurrence ?? {};
  const overrides = occurrence?.overrides || {};
  const pick = (field) => overrides[field] ?? series[field];
  return {
    title: pick("title") ?? "",
    description: pick("description") ?? "",
    location: pick("location") ?? "",
    is_private: Boolean(pick("is_private")),
    is_all_day: Boolean(pick("is_all_day")),
    starts_at: occurrence?.starts_at ?? event?.starts_at,
    ends_at: occurrence?.ends_at ?? event?.ends_at,
    timezone: series.timezone ?? occurrence?.timezone,
    isCanceled: isCanceled(occurrence),
  };
}

// How an occurrence relates to its Event (mirrors iOS OccurrenceRole). A
// non-recurring Event isn't necessarily a single occurrence: one that stopped
// repeating keeps retained occurrences (history + customized ones).
//   "series"              — an occurrence of a repeating Event
//   "oneOff"              — a true one-off's own occurrence
//   "formerSeriesPrimary" — a formerly recurring Event's own occurrence
//   "retained"            — a retained occurrence of a formerly recurring Event
export function occurrenceRole(event, occurrence) {
  if (isRecurring(event)) return "series";
  let isPrimary = true;
  if (occurrence) {
    if (typeof occurrence.is_primary === "boolean") isPrimary = occurrence.is_primary;
    else if (event?.primary_occurrence_id != null) isPrimary = occurrence.id === event.primary_occurrence_id;
    else isPrimary = !isCanceled(occurrence);
  }
  if (!isPrimary) return "retained";
  return event?.has_retained_occurrences ? "formerSeriesPrimary" : "oneOff";
}

// Removal actions, in menu order.
export const REMOVAL = {
  CANCEL_OCCURRENCE: "cancelOccurrence", // POST /event-occurrences/{id}/cancel/
  DELETE_FUTURE: "deleteFuture", // DELETE /events/{id}/?scope=future
  DELETE_EVENT: "deleteEvent", // DELETE /events/{id}/
  REMOVE_FROM_CALENDAR: "removeFromCalendar", // DELETE /events/{id}/rsvp/
  // A guest declines this date only: POST /event-occurrences/{id}/rsvp/
  // {"status": "declined"}; the series RSVP is untouched.
  DECLINE_OCCURRENCE: "declineOccurrence",
  // A guest of a repeating series declines the series: POST /events/{id}/rsvp/
  // {"status": "declined"}. Dates they answered on their own keep those
  // answers (the backend never clears occurrence RSVPs here).
  DECLINE_SERIES: "declineSeries",
};

// Which actions the detail view offers (mirrors iOS EventDetailActions).
//   edit: null | "event" (Event form) | "occurrence" (occurrence form) |
//         "chooseScope" (This Occurrence / Entire Series)
// A canceled occurrence is read-only for everyone. A guest's removals are
// their own participation only: declining this date (when its participation
// is resolved) or the series — never one in place of the other; a one-off
// keeps Remove From Calendar.
export function eventDetailActions({ event, occurrence = null, isOwner }) {
  const active = !isCanceled(occurrence);
  const status = myRsvpStatus(event);
  const role = occurrenceRole(event, occurrence);

  const deleteEventLabel =
    role === "series"
      ? "Delete This Series"
      : role === "oneOff"
      ? "Delete Event"
      : "Delete Entire Event";

  let removals = [];
  if (!active) {
    removals = [];
  } else if (!isOwner) {
    const p = occurrenceParticipation(occurrence);
    if (role !== "oneOff" && occurrence && p && !p.isOccurrenceOnly && p.status === "going") {
      removals.push(REMOVAL.DECLINE_OCCURRENCE);
    }
    if (status === "going") {
      removals.push(role === "series" ? REMOVAL.DECLINE_SERIES : REMOVAL.REMOVE_FROM_CALENDAR);
    }
  } else if (role === "series") {
    // This date or the series. The backend cancels a single occurrence (it
    // stays, marked canceled) rather than deleting it.
    removals = occurrence
      ? [REMOVAL.CANCEL_OCCURRENCE, REMOVAL.DELETE_EVENT]
      : [REMOVAL.DELETE_FUTURE, REMOVAL.DELETE_EVENT];
  } else if (role === "retained" && occurrence) {
    removals = [REMOVAL.CANCEL_OCCURRENCE, REMOVAL.DELETE_EVENT];
  } else {
    // A true one-off or a former series' own occurrence: the backend cancels
    // neither (delete_event_instead) — the Event is deleted.
    removals = [REMOVAL.DELETE_EVENT];
  }

  let edit = null;
  if (active && isOwner) {
    if (role === "series" && occurrence) edit = "chooseScope";
    else if (role === "retained" && occurrence) edit = "occurrence";
    else edit = "event";
  }

  return {
    role,
    edit,
    removals,
    deleteEventLabel,
    canInviteFriends: active && isOwner,
    // The series invitation's Going / Can't go, while it's pending.
    showsInvitationResponse: active && !isOwner && status === "invited",
    showsReminder: active && status === "going",
  };
}

// "Repeats weekly", "Repeats every 2 weeks", …; null when it doesn't repeat
// or the value isn't recognized (iOS EventRecurrenceOption.repeatsLabel).
export function repeatsLabel(recurrence) {
  if (!recurrence || recurrence === "none") return null;
  const label = recurrenceLabel(recurrence);
  return label ? `Repeats ${label.toLowerCase()}` : null;
}

export const INVITE_TARGET = { SERIES: "series", OCCURRENCE: "occurrence" };

// Which sections the detail shows (mirrors iOS EventDetailLayout):
//   reminderRow     the one Reminder row: "occurrence" (this date's effective
//                   reminder, see occurrenceParticipation.reminderEdit),
//                   "event" (the series RSVP's — a one-off, the Event at its
//                   own dates, or a payload without resolved participation)
//                   or "none"
//   showsOccurrenceParticipation  this date's participation drives the
//                   Reminder row (never a separate "This Date" RSVP card)
//   repeatsLabel    the Series row under Date & Time, or null
//   offersChangeToGoing  "Change RSVP to Going": a guest who declined this
//                   date on its own (not a series decline, not canceled)
//   inviteScopes    what Invite Friends offers once friends are picked:
//                   [series] sends right away; two ask first; [] no button
export function eventDetailLayout({ event, occurrence = null, isOwner }) {
  const actions = eventDetailActions({ event, occurrence, isOwner });
  const role = actions.role;
  const p = occurrenceParticipation(occurrence);
  const showsOccurrenceParticipation =
    Boolean(occurrence && p) &&
    (role !== "oneOff" || p.hasStatusOverride || p.hasReminderOverride || p.isOccurrenceOnly);

  let reminderRow;
  if (showsOccurrenceParticipation) {
    reminderRow = !isCanceled(occurrence) && p.status === "going" ? "occurrence" : "none";
  } else {
    reminderRow = actions.showsReminder ? "event" : "none";
  }

  let inviteScopes = [];
  if (actions.canInviteFriends) {
    inviteScopes =
      occurrence && role !== "oneOff" && !isCanceled(occurrence)
        ? [INVITE_TARGET.SERIES, INVITE_TARGET.OCCURRENCE]
        : [INVITE_TARGET.SERIES];
  }

  return {
    showsOccurrenceParticipation,
    reminderRow,
    repeatsLabel: p?.isOccurrenceOnly ? null : repeatsLabel(event?.recurrence),
    offersChangeToGoing: !isOwner && isDeclinedOnItsOwn(occurrence),
    inviteScopes,
  };
}

// The occurrence an "Entire Series" edit opens from. The Event form takes its
// times from it (the backend applies that wall-clock time to the series), so a
// rescheduled occurrence would carry its custom time into the series — such an
// edit opens at the series' own values instead.
export function seriesEditOccurrence(occurrence) {
  return occurrence && !occurrence.is_override ? occurrence : null;
}

export function removalMenuLabel(action, deleteEventLabel) {
  switch (action) {
    case REMOVAL.CANCEL_OCCURRENCE:
      return "Cancel This Occurrence";
    case REMOVAL.DELETE_FUTURE:
      return "Delete All Future Occurrences";
    case REMOVAL.DELETE_EVENT:
      return deleteEventLabel;
    case REMOVAL.REMOVE_FROM_CALENDAR:
      return "Remove From Calendar";
    case REMOVAL.DECLINE_OCCURRENCE:
      return "Decline This Date";
    case REMOVAL.DECLINE_SERIES:
      return "Decline This Series";
    default:
      return "";
  }
}

// Confirmation copy (iOS EventRemovalAction.confirmation).
export function removalConfirmation(action, deleteEventLabel) {
  switch (action) {
    case REMOVAL.CANCEL_OCCURRENCE:
      return {
        title: "Cancel This Occurrence?",
        message:
          "It stays on the calendar, marked as canceled, and can't be changed afterward. Guests who haven't declined will be notified.",
        confirmLabel: "Cancel Occurrence",
        dismissLabel: "Don't Cancel",
      };
    case REMOVAL.DELETE_FUTURE:
      return {
        title: "Delete All Future Occurrences?",
        message:
          "Every occurrence that hasn't started yet will be permanently deleted, including customized and canceled ones, and the series will end. Past occurrences stay. Guests are notified if anything they could still attend is removed.",
        confirmLabel: "Delete Future Occurrences",
        dismissLabel: "Keep",
      };
    case REMOVAL.DELETE_EVENT:
      if (deleteEventLabel === "Delete Event") {
        return {
          title: "Delete Event?",
          message: "This will permanently delete this event for everyone.",
          confirmLabel: "Delete",
          dismissLabel: "Cancel",
        };
      }
      return {
        title: `${deleteEventLabel}?`,
        message:
          "This will permanently delete the event and all of its occurrences, past and future, for everyone.",
        confirmLabel: "Delete",
        dismissLabel: "Cancel",
      };
    case REMOVAL.REMOVE_FROM_CALENDAR:
      return {
        title: "Remove Event?",
        message: "This will remove this event from your calendar.",
        confirmLabel: "Remove",
        dismissLabel: "Cancel",
      };
    case REMOVAL.DECLINE_OCCURRENCE:
      return {
        title: "Decline This Date?",
        message: "You'll be marked as not going to this date only. Your other dates stay as they are.",
        confirmLabel: "Decline",
        dismissLabel: "Cancel",
      };
    case REMOVAL.DECLINE_SERIES:
      return {
        title: "Decline This Series?",
        message: "Dates you've responded to individually will keep their current RSVP.",
        confirmLabel: "Decline Series",
        dismissLabel: "Cancel",
      };
    default:
      return null;
  }
}

// Calendar shows what the user created or is going to, and a date a series
// participant declined on its own (subdued, like a canceled one); pending
// invitations stay in the invite inbox and other declines are hidden (as on
// iOS). The backend returns any occurrence the user has an RSVP on. See
// occurrenceParticipation.isCalendarVisible.
export function isShownOnCalendar(occurrence) {
  return isCalendarVisible(occurrence);
}
